/**
 * Shared, reference-counted Cesium World Terrain + depthTestAgainstTerrain context.
 *
 * Multiple 3D layers (#177 3D Flood Inundation, #178 Camera Line-of-Sight Viewshed,
 * and future #179/#180) all need the SAME globe upgrade — Cesium World Terrain with
 * `depthTestAgainstTerrain = true` — and all want to restore the operator's original
 * globe when they switch off. Before this module each layer did the swap/restore with
 * its OWN private `priorTerrain` / `priorDepthTest` / `terrainSwapped` closure vars, so
 * whichever layer toggled OFF first ripped World Terrain out from under any other layer
 * still relying on it.
 *
 * We are committing to COEXISTENCE, not mutual exclusion: route every layer through
 * here. A simple reference count per viewer means:
 *   - The FIRST acquire (0 → 1) captures the original terrain + depth-test flag ONCE
 *     and installs World Terrain + depthTest.
 *   - Further acquires only bump the count (the globe is already upgraded).
 *   - Each release decrements; only the LAST release (1 → 0) restores the captured
 *     original. So the globe upgrade survives until the last 3D layer is off.
 *
 * State is keyed per Cesium.Viewer via a WeakMap, so a torn-down viewer's entry is
 * garbage-collected and a fresh viewer starts clean (no cross-viewer bleed).
 *
 * NOTE: callers create the World Terrain provider themselves (an async, ion-gated call
 * that throws without a token) and pass it in. That keeps this module pure + unit-
 * testable with a fake provider and lets each caller degrade gracefully on its own.
 */
import type * as Cesium from 'cesium';

interface TerrainRefState {
  /** Number of layers currently holding World Terrain on this viewer. */
  count: number;
  /** The operator's terrain provider captured at the 0 → 1 transition. */
  priorTerrain: Cesium.TerrainProvider;
  /** The operator's depthTestAgainstTerrain captured at the 0 → 1 transition. */
  priorDepthTest: boolean;
}

const STATE = new WeakMap<Cesium.Viewer, TerrainRefState>();

/**
 * Acquire World Terrain on `viewer`, installing the provided `worldTerrain` provider +
 * `depthTestAgainstTerrain = true` on the first (0 → 1) acquire and capturing the
 * original globe state so it can be restored by the last release. Subsequent acquires
 * are a no-op beyond bumping the reference count — the globe is already upgraded.
 *
 * Idempotency is the CALLER's responsibility: a single layer must acquire exactly once
 * (track a per-layer "holding" flag) and pair every acquire with one release. Calling
 * acquire twice from the same layer would leak a reference and pin the globe.
 */
export function acquireWorldTerrain(
  viewer: Cesium.Viewer,
  worldTerrain: Cesium.TerrainProvider,
): void {
  if (viewer.isDestroyed?.()) return;
  const existing = STATE.get(viewer);
  if (existing) {
    existing.count += 1;
    return;
  }
  // 0 → 1: capture the operator's globe ONCE, then upgrade it.
  STATE.set(viewer, {
    count: 1,
    priorTerrain: viewer.terrainProvider,
    priorDepthTest: viewer.scene.globe.depthTestAgainstTerrain,
  });
  viewer.terrainProvider = worldTerrain;
  viewer.scene.globe.depthTestAgainstTerrain = true;
}

/**
 * Release one World Terrain reference on `viewer`. Only the LAST release (1 → 0)
 * restores the captured original terrain + depth-test flag; earlier releases just
 * decrement. A release with no outstanding acquire is a safe no-op (so a layer that
 * never acquired — e.g. skipped the swap or failed to load the provider — can still
 * call release unconditionally on teardown).
 */
export function releaseWorldTerrain(viewer: Cesium.Viewer): void {
  const state = STATE.get(viewer);
  if (!state) return;
  state.count -= 1;
  if (state.count > 0) return;
  // 1 → 0: last holder gone — restore the operator's original globe.
  STATE.delete(viewer);
  if (viewer.isDestroyed?.()) return;
  viewer.terrainProvider = state.priorTerrain;
  viewer.scene.globe.depthTestAgainstTerrain = state.priorDepthTest;
}

/** Test/inspection helper: current World Terrain reference count for a viewer. */
export function worldTerrainRefCount(viewer: Cesium.Viewer): number {
  return STATE.get(viewer)?.count ?? 0;
}
